import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import {
  Wrench,
  MapPin,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Camera,
  Upload,
  Loader2,
  Shield,
  Calendar,
  User,
  Building2,
  FileText,
  Send,
  Navigation,
  Play,
  Pause,
  Check,
  DollarSign,
  Ban,
  Info,
  MessageCircle,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Sterile Mate Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

// Status steps for progress tracker
const STATUS_STEPS = [
  { key: "sent", label: "Dispatched", icon: Send },
  { key: "acknowledged", label: "Acknowledged", icon: Check },
  { key: "en_route", label: "En Route", icon: Navigation },
  { key: "on_site", label: "On Site", icon: MapPin },
  { key: "completed", label: "Completed", icon: CheckCircle2 }
];

export default function ContractorJobPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [notes, setNotes] = useState("");
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState({ amount: "", description: "", warranty_override_code: "" });
  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  const [showOverrideInput, setShowOverrideInput] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  
  // GPS Check-in/Check-out state
  const [gpsStatus, setGpsStatus] = useState({ isCheckedIn: false, activeCheckin: null });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [checkOutNotes, setCheckOutNotes] = useState("");
  
  // Contractor Sign-off state
  const [signOffStatus, setSignOffStatus] = useState(null); // complete, pending, still_needs_repair
  const [signOffNotes, setSignOffNotes] = useState("");
  const [submittingSignOff, setSubmittingSignOff] = useState(false);

  useEffect(() => {
    fetchJobData();
  }, [token]);

  const fetchJobData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/contractor/job/${token}`);
      setJobData(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 410) {
        setError("This dispatch link has expired. Please contact Sterile Mate for a new link.");
      } else if (err.response?.status === 404) {
        setError("Invalid dispatch link. Please check your email for the correct link.");
      } else {
        setError("Failed to load job details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // GPS Functions
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let message = "Unable to get your location";
          if (error.code === 1) message = "Location permission denied. Please enable location access.";
          else if (error.code === 2) message = "Location unavailable. Please try again.";
          else if (error.code === 3) message = "Location request timed out. Please try again.";
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const handleGPSCheckIn = async () => {
    try {
      setGpsLoading(true);
      setLocationError(null);
      
      // Get current location
      const location = await getCurrentLocation();
      setCurrentLocation(location);
      
      // Make check-in request
      const contractorToken = localStorage.getItem("contractor_token");
      const res = await axios.post(
        `${API}/contractor/gps/check-in`,
        {
          job_id: jobData?.job?.id || jobData?.job?.ticket_number,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        { headers: { Authorization: `Bearer ${contractorToken}` } }
      );
      
      setGpsStatus({ isCheckedIn: true, activeCheckin: res.data.time_log });
      toast.success("Checked in successfully!");
      
      // Refresh job data
      fetchJobData();
      
    } catch (err) {
      console.error("Check-in error:", err);
      if (err.message?.includes("Location")) {
        setLocationError(err.message);
        toast.error(err.message);
      } else {
        toast.error(err.response?.data?.detail || "Failed to check in. Please try again.");
      }
    } finally {
      setGpsLoading(false);
    }
  };

  const handleGPSCheckOut = async () => {
    try {
      setGpsLoading(true);
      setLocationError(null);
      
      // Get current location
      const location = await getCurrentLocation();
      
      // Make check-out request
      const contractorToken = localStorage.getItem("contractor_token");
      const res = await axios.post(
        `${API}/contractor/gps/check-out`,
        {
          job_id: jobData?.job?.id || jobData?.job?.ticket_number,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          work_summary: checkOutNotes
        },
        { headers: { Authorization: `Bearer ${contractorToken}` } }
      );
      
      setGpsStatus({ isCheckedIn: false, activeCheckin: null });
      setCheckOutNotes("");
      toast.success(`Checked out! Time on site: ${res.data.duration_formatted}`);
      
      // Refresh job data
      fetchJobData();
      
    } catch (err) {
      console.error("Check-out error:", err);
      if (err.message?.includes("Location")) {
        setLocationError(err.message);
        toast.error(err.message);
      } else {
        toast.error(err.response?.data?.detail || "Failed to check out. Please try again.");
      }
    } finally {
      setGpsLoading(false);
    }
  };

  // Fetch GPS status on load
  useEffect(() => {
    const fetchGPSStatus = async () => {
      const contractorToken = localStorage.getItem("contractor_token");
      if (!contractorToken) return;
      
      try {
        const res = await axios.get(`${API}/contractor/gps/current-status`, {
          headers: { Authorization: `Bearer ${contractorToken}` }
        });
        setGpsStatus({
          isCheckedIn: res.data.is_checked_in,
          activeCheckin: res.data.active_checkin
        });
      } catch (err) {
        console.log("Could not fetch GPS status:", err.message);
      }
    };
    
    if (jobData) {
      fetchGPSStatus();
    }
  }, [jobData]);

  // Contractor Sign-off function
  const handleContractorSignOff = async (status) => {
    try {
      setSubmittingSignOff(true);
      const contractorToken = localStorage.getItem("contractor_token");
      const jobId = jobData?.job?.id || jobData?.ticket?.id;
      
      const res = await axios.post(
        `${API}/maintenance-requests/${jobId}/sign-off`,
        {
          status: status,
          notes: signOffNotes
        },
        { headers: { Authorization: `Bearer ${contractorToken}` } }
      );
      
      setSignOffStatus(status);
      toast.success(`Sign-off recorded: ${status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}`);
      
      if (res.data.can_close_job) {
        toast.success("All parties have signed off. Job can now be closed.");
      }
      
      // Refresh job data
      fetchJobData();
      
    } catch (err) {
      console.error("Sign-off error:", err);
      toast.error(err.response?.data?.detail || "Failed to submit sign-off");
    } finally {
      setSubmittingSignOff(false);
    }
  };

  const fetchChatMessages = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/job/${ticketId}/chat`);
      setChatMessages(res.data.messages || []);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("Failed to fetch chat:", err);
    }
  };

  // WebSocket connection for real-time chat
  const wsRef = useRef(null);
  
  const connectWebSocket = (ticketId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '');
    const contractorToken = localStorage.getItem("contractor_token");
    
    wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${ticketId}?token=${contractorToken}`);
    
    wsRef.current.onopen = () => {
      console.log("WebSocket connected for chat");
    };
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "new_message") {
        setChatMessages(prev => [...prev, data.message]);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    };
    
    wsRef.current.onclose = () => {
      console.log("WebSocket disconnected");
    };
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !jobData?.ticket?.id) return;
    try {
      setSendingMessage(true);
      
      // Try WebSocket first, fall back to HTTP
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          message: newMessage.trim()
        }));
        setNewMessage("");
      } else {
        await axios.post(`${API}/job/${jobData.ticket.id}/chat`, {
          message: newMessage.trim()
        });
        setNewMessage("");
        fetchChatMessages(jobData.ticket.id);
      }
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const updateStatus = async (newStatus) => {
    try {
      setUpdating(true);
      
      // Get GPS location for check-in/check-out
      let location = null;
      if (newStatus === "on_site" || newStatus === "completed") {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            });
          });
          location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          toast.success(`Location captured: ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
        } catch (geoErr) {
          console.warn("Could not get GPS location:", geoErr);
          toast.warning("GPS unavailable - location not recorded");
        }
      }
      
      await axios.put(`${API}/contractor/job/${token}/status`, {
        status: newStatus,
        notes: notes || undefined,
        check_in_time: newStatus === "on_site" ? new Date().toISOString() : undefined,
        check_out_time: newStatus === "completed" ? new Date().toISOString() : undefined,
        check_in_location: newStatus === "on_site" ? location : undefined,
        check_out_location: newStatus === "completed" ? location : undefined
      });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      setNotes("");
      fetchJobData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setPhotoUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/maintenance/upload`, formData);
        setCompletionPhotos(prev => [...prev, res.data.url]);
        toast.success("Photo uploaded");
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setPhotoUploading(false);
  };

  const submitCompletionPhotos = async () => {
    if (completionPhotos.length === 0) {
      toast.error("Please upload at least one completion photo");
      return;
    }
    try {
      setUpdating(true);
      await axios.put(`${API}/contractor/job/${token}/status`, {
        completion_photo_urls: completionPhotos
      });
      toast.success("Completion photos saved");
      fetchJobData();
    } catch (err) {
      toast.error("Failed to save photos");
    } finally {
      setUpdating(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#1e40af";
    ctx.lineWidth = 2;
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!hasSignature || !signatureName) {
      toast.error("Please provide signature and signer name");
      return;
    }
    try {
      setUpdating(true);
      const canvas = signatureCanvasRef.current;
      const signatureData = canvas.toDataURL();
      await axios.put(`${API}/contractor/job/${token}/status`, {
        hospital_signature: signatureData,
        hospital_signer_name: signatureName
      });
      toast.success("Hospital sign-off recorded");
      fetchJobData();
    } catch (err) {
      toast.error("Failed to save signature");
    } finally {
      setUpdating(false);
    }
  };

  const submitInvoice = async () => {
    if (!invoiceData.amount || !invoiceData.description) {
      toast.error("Please enter amount and description");
      return;
    }
    
    // For warranty items, require override code
    if (is_warranty && !invoiceData.warranty_override_code) {
      setShowOverrideInput(true);
      toast.error("Override code required for warranty items");
      return;
    }
    
    try {
      setSubmittingInvoice(true);
      await axios.post(`${API}/contractor/job/${token}/invoice`, {
        ticket_id: jobData.ticket.id,
        amount: parseFloat(invoiceData.amount),
        description: invoiceData.description,
        warranty_override_code: invoiceData.warranty_override_code || undefined
      });
      toast.success("Invoice submitted successfully");
      setShowInvoice(false);
      setShowOverrideInput(false);
      fetchJobData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to submit invoice";
      if (errorMsg.includes("WARRANTY_OVERRIDE_REQUIRED")) {
        setShowOverrideInput(true);
        toast.error("Admin override code required for warranty items");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSubmittingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Access Error</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">Contact: info@sterilemate.com | 800.988.3892</p>
        </div>
      </div>
    );
  }

  const { ticket, dispatch, contractor, is_warranty, warranty_message } = jobData;
  const currentStatus = ticket.work_status || dispatch.status;
  const currentStepIndex = STATUS_STEPS.findIndex(s => s.key === currentStatus);
  const isCompleted = currentStatus === "completed";
  const hasSignedOff = !!ticket.hospital_signature;
  const hasCompletionPhotos = ticket.completion_photos?.length > 0;
  const canSubmitInvoice = isCompleted && hasSignedOff && hasCompletionPhotos && !ticket.contractor_invoice;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Sterile Mate" className="h-10 object-contain" />
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">Ticket #{ticket.id}</p>
            <p className="text-xs text-slate-500">{contractor?.company_name}</p>
          </div>
        </div>
      </header>

      {/* Warranty Banner */}
      {is_warranty && (
        <div className="bg-amber-500 text-white">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <Shield className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-bold">⚠️ WARRANTY ITEM - NO CHARGES PERMITTED</p>
              <p className="text-sm opacity-90">This equipment is under active warranty. No labor or parts charges allowed without admin override.</p>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Progress */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-4">Job Progress</h2>
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStepIndex;
              const isComplete = index < currentStepIndex;
              return (
                <div key={step.key} className="flex flex-col items-center flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center mb-2
                    ${isComplete ? "bg-green-500 text-white" : isActive ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-400"}
                  `}>
                    <StepIcon className="w-5 h-5" />
                  </div>
                  <span className={`text-xs text-center ${isActive ? "font-semibold text-blue-600" : "text-slate-500"}`}>
                    {step.label}
                  </span>
                  {index < STATUS_STEPS.length - 1 && (
                    <div className={`absolute h-1 w-full top-5 left-1/2 -z-10 ${isComplete ? "bg-green-500" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Job Details */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Job Details
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-2">Issue</h3>
              <p className="text-lg font-semibold text-slate-900">{ticket.issue_type_label}</p>
              <p className="text-slate-600 mt-1">{ticket.description}</p>
              <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                ticket.priority === "urgent" ? "bg-red-100 text-red-700" :
                ticket.priority === "high" ? "bg-orange-100 text-orange-700" :
                "bg-blue-100 text-blue-700"
              }`}>
                {ticket.priority?.toUpperCase()} Priority
              </span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-slate-500 mb-2">Location</h3>
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-slate-400 mt-1" />
                <div>
                  <p className="font-medium text-slate-900">{ticket.facility?.name}</p>
                  <p className="text-slate-600">{ticket.facility?.address}</p>
                  {ticket.facility?.location_details && (
                    <p className="text-slate-500 text-sm">{ticket.facility.location_details}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h3 className="text-sm font-medium text-slate-500 mb-3">Contact</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">{ticket.contact?.name}</span>
              </div>
              <a href={`tel:${ticket.contact?.phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                <Phone className="w-4 h-4" />
                {ticket.contact?.phone}
              </a>
              <a href={`mailto:${ticket.contact?.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                <Mail className="w-4 h-4" />
                {ticket.contact?.email}
              </a>
            </div>
          </div>

          {/* Asset Info */}
          {ticket.asset && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Equipment Details</h3>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{ticket.asset.name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  {ticket.asset.serial_number && <p><span className="text-slate-500">S/N:</span> {ticket.asset.serial_number}</p>}
                  {ticket.asset.model_number && <p><span className="text-slate-500">Model:</span> {ticket.asset.model_number}</p>}
                  {ticket.asset.install_date && <p><span className="text-slate-500">Installed:</span> {new Date(ticket.asset.install_date).toLocaleDateString()}</p>}
                  {ticket.asset.last_service_date && <p><span className="text-slate-500">Last Service:</span> {new Date(ticket.asset.last_service_date).toLocaleDateString()}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Issue Photos */}
          {ticket.photo_urls?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Issue Photos</h3>
              <div className="grid grid-cols-3 gap-2">
                {ticket.photo_urls.map((url, i) => (
                  <img key={i} src={url} alt={`Issue ${i+1}`} className="rounded-lg w-full h-24 object-cover" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* GPS Check-In/Check-Out Section */}
        {!isCompleted && (
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 shadow-sm border border-blue-200">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              GPS Time Tracking
            </h2>
            
            {locationError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Location Error</p>
                  <p>{locationError}</p>
                </div>
              </div>
            )}
            
            {gpsStatus.isCheckedIn && gpsStatus.activeCheckin ? (
              <div className="space-y-4">
                {/* Active Check-in Info */}
                <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">Currently Checked In</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                    <div>
                      <span className="text-green-600">Check-in Time:</span>
                      <p className="font-medium">{new Date(gpsStatus.activeCheckin.check_in_time).toLocaleTimeString()}</p>
                    </div>
                    <div>
                      <span className="text-green-600">Duration:</span>
                      <p className="font-medium">{gpsStatus.activeCheckin.current_duration_formatted || "Calculating..."}</p>
                    </div>
                  </div>
                </div>
                
                {/* Check-out Form */}
                <div className="space-y-3">
                  <Textarea
                    value={checkOutNotes}
                    onChange={(e) => setCheckOutNotes(e.target.value)}
                    placeholder="Work summary (optional)..."
                    rows={2}
                    className="border-slate-200"
                  />
                  <Button 
                    onClick={handleGPSCheckOut}
                    disabled={gpsLoading}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    {gpsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <MapPin className="w-4 h-4 mr-2" />
                    )}
                    Check Out
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Tap the button below when you arrive at the job site. Your GPS location will be recorded for verification.
                </p>
                <Button 
                  onClick={handleGPSCheckIn}
                  disabled={gpsLoading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {gpsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <MapPin className="w-4 h-4 mr-2" />
                  )}
                  Check In at Job Site
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Your location will be verified against the facility address
                </p>
              </div>
            )}
          </div>
        )}

        {/* Status Update Actions */}
        {!isCompleted && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-4">Update Status</h2>
            <div className="space-y-4">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this update (optional)..."
                rows={2}
                className="border-slate-200"
              />
              <div className="flex flex-wrap gap-2">
                {currentStatus === "sent" && (
                  <Button onClick={() => updateStatus("acknowledged")} disabled={updating} className="bg-blue-600 hover:bg-blue-700">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Accept Job
                  </Button>
                )}
                {currentStatus === "acknowledged" && (
                  <Button onClick={() => updateStatus("en_route")} disabled={updating} className="bg-blue-600 hover:bg-blue-700">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Navigation className="w-4 h-4 mr-2" />}
                    Start Travel
                  </Button>
                )}
                {currentStatus === "en_route" && (
                  <Button onClick={() => updateStatus("on_site")} disabled={updating} className="bg-green-600 hover:bg-green-700">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MapPin className="w-4 h-4 mr-2" />}
                    Check In On Site
                  </Button>
                )}
                {currentStatus === "on_site" && (
                  <Button onClick={() => updateStatus("completed")} disabled={updating} className="bg-emerald-600 hover:bg-emerald-700">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Mark Complete
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Completion Section */}
        {isCompleted && (
          <>
            {/* Completion Photos */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-600" />
                Work Completed Photos {hasCompletionPhotos ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <span className="text-xs text-red-500">*Required</span>}
              </h2>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {(ticket.completion_photos || completionPhotos).map((url, i) => (
                  <img key={i} src={url} alt={`Completion ${i+1}`} className="rounded-lg w-full h-24 object-cover" />
                ))}
                {!hasCompletionPhotos && (
                  <label className="border-2 border-dashed border-slate-300 rounded-lg h-24 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500">
                    <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                    {photoUploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : <Camera className="w-5 h-5 text-slate-400" />}
                    <span className="text-xs text-slate-500 mt-1">Add Photo</span>
                  </label>
                )}
              </div>
              {!hasCompletionPhotos && completionPhotos.length > 0 && (
                <Button onClick={submitCompletionPhotos} disabled={updating} className="bg-blue-600 hover:bg-blue-700">
                  {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Completion Photos
                </Button>
              )}
            </div>

            {/* Hospital Sign-off */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Hospital Sign-off {hasSignedOff ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <span className="text-xs text-red-500">*Required</span>}
              </h2>
              {hasSignedOff ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">Signed by: {ticket.hospital_signer_name}</p>
                  <img src={ticket.hospital_signature} alt="Signature" className="mt-2 max-h-20" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Hospital Staff Name</Label>
                    <Input
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      placeholder="Enter name of person signing..."
                      className="mt-1 border-slate-200"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Signature</Label>
                    <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
                      <canvas
                        ref={signatureCanvasRef}
                        width={400}
                        height={150}
                        className="w-full bg-white cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button variant="outline" size="sm" onClick={clearSignature}>Clear</Button>
                      <Button size="sm" onClick={submitSignature} disabled={updating || !hasSignature || !signatureName} className="bg-blue-600 hover:bg-blue-700">
                        {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Submit Sign-off
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contractor Sign-Off Section */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 shadow-sm border border-purple-200">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                Contractor Job Sign-Off
                {signOffStatus && <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                  signOffStatus === 'complete' ? 'bg-green-100 text-green-700' :
                  signOffStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>{signOffStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>}
              </h2>
              
              <p className="text-sm text-slate-600 mb-4">
                Select the status of this job. All parties (Hospital, Contractor, Supplier) must sign off for the job to be officially closed.
              </p>
              
              <div className="space-y-4">
                <Textarea
                  value={signOffNotes}
                  onChange={(e) => setSignOffNotes(e.target.value)}
                  placeholder="Add notes about the job status (optional)..."
                  rows={2}
                  className="border-purple-200"
                />
                
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    onClick={() => handleContractorSignOff('complete')}
                    disabled={submittingSignOff}
                    className="bg-green-600 hover:bg-green-700 flex-col h-auto py-3"
                  >
                    {submittingSignOff ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                    <span className="text-xs mt-1">Complete</span>
                  </Button>
                  
                  <Button 
                    onClick={() => handleContractorSignOff('pending')}
                    disabled={submittingSignOff}
                    className="bg-amber-500 hover:bg-amber-600 flex-col h-auto py-3"
                  >
                    {submittingSignOff ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
                    <span className="text-xs mt-1">Pending</span>
                  </Button>
                  
                  <Button 
                    onClick={() => handleContractorSignOff('still_needs_repair')}
                    disabled={submittingSignOff}
                    className="bg-red-500 hover:bg-red-600 flex-col h-auto py-3"
                  >
                    {submittingSignOff ? <Loader2 className="w-5 h-5 animate-spin" /> : <AlertTriangle className="w-5 h-5" />}
                    <span className="text-xs mt-1">Needs Repair</span>
                  </Button>
                </div>
                
                <p className="text-xs text-purple-600 text-center">
                  All tagged parties will be notified of your sign-off status
                </p>
              </div>
            </div>

            {/* Invoice Submission */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                Invoice Submission
              </h2>
              
              {is_warranty && !showOverrideInput ? (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <Ban className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Billing Restricted - Warranty Item</p>
                      <p className="text-sm text-amber-700">This equipment is under warranty. Admin override code required for billing.</p>
                    </div>
                  </div>
                  {canSubmitInvoice && (
                    <Button onClick={() => setShowOverrideInput(true)} variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50">
                      <Shield className="w-4 h-4 mr-2" />
                      I Have an Override Code
                    </Button>
                  )}
                </div>
              ) : ticket.contractor_invoice ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Invoice Submitted
                  </p>
                  <p className="text-sm text-green-700 mt-1">Amount: ${ticket.contractor_cost?.toFixed(2)}</p>
                </div>
              ) : !canSubmitInvoice ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">Complete These Steps First:</p>
                    <ul className="text-sm text-slate-600 mt-1 space-y-1">
                      <li className={hasCompletionPhotos ? "text-green-600" : ""}>
                        {hasCompletionPhotos ? "✓" : "○"} Upload completion photos
                      </li>
                      <li className={hasSignedOff ? "text-green-600" : ""}>
                        {hasSignedOff ? "✓" : "○"} Get hospital sign-off
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Warranty Override Code Input */}
                  {(is_warranty || showOverrideInput) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <Label className="text-amber-800 font-medium">Admin Override Code</Label>
                      <Input
                        value={invoiceData.warranty_override_code}
                        onChange={(e) => setInvoiceData({ ...invoiceData, warranty_override_code: e.target.value.toUpperCase() })}
                        placeholder="Enter 8-character code"
                        className="mt-2 border-amber-300 bg-white font-mono text-center text-lg tracking-wider"
                        maxLength={8}
                      />
                      <p className="text-xs text-amber-700 mt-2">Contact Sterile Mate admin for override code</p>
                    </div>
                  )}
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Invoice Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={invoiceData.amount}
                        onChange={(e) => setInvoiceData({ ...invoiceData, amount: e.target.value })}
                        placeholder="0.00"
                        className="mt-1 border-slate-200"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700">Work Description</Label>
                    <Textarea
                      value={invoiceData.description}
                      onChange={(e) => setInvoiceData({ ...invoiceData, description: e.target.value })}
                      placeholder="Describe the work performed..."
                      rows={3}
                      className="mt-1 border-slate-200"
                    />
                  </div>
                  <Button onClick={submitInvoice} disabled={submittingInvoice} className="bg-green-600 hover:bg-green-700">
                    {submittingInvoice ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Invoice
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Chat FAB Button */}
      <button
        onClick={() => { 
          setShowChat(!showChat); 
          if (!showChat && jobData?.ticket?.id) {
            fetchChatMessages(jobData.ticket.id);
            connectWebSocket(jobData.ticket.id);
          }
        }}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors z-50"
      >
        {showChat ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Panel */}
      {showChat && (
        <div className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 flex flex-col max-h-[60vh]">
          <div className="bg-blue-600 text-white px-4 py-3 rounded-t-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">Chat with Admin</span>
            </div>
            <button onClick={() => setShowChat(false)} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[300px] bg-slate-50">
            {chatMessages.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">No messages yet. Start a conversation with admin.</p>
            ) : (
              chatMessages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender_type === "contractor" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                    msg.sender_type === "contractor" 
                      ? "bg-blue-600 text-white" 
                      : "bg-white border border-slate-200 text-slate-800"
                  }`}>
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.sender_type === "contractor" ? "text-blue-200" : "text-slate-400"}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          
          <div className="p-3 border-t border-slate-200 bg-white rounded-b-xl">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
                onKeyPress={(e) => e.key === "Enter" && sendChatMessage()}
              />
              <Button onClick={sendChatMessage} disabled={sendingMessage || !newMessage.trim()} size="sm" className="bg-blue-600 hover:bg-blue-700">
                {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-slate-500">
          <p>Questions? Contact Sterile Mate Support</p>
          <p className="mt-1">
            <a href="tel:800-988-3892" className="text-blue-600 hover:underline">800.988.3892</a>
            {" | "}
            <a href="mailto:info@sterilemate.com" className="text-blue-600 hover:underline">info@sterilemate.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
